import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query/react';
import { sessionEstablished, sessionExpired as sessionExpiredAction } from '@/store/authSlice';
import { normalizeStageDetail } from './stageDetailNormalize';
import type {
  ApprovalDecideRequest,
  RaiseDoubtRequest,
  RaiseDoubtResponse,
  DoubtStatusResponse,
  DecideDoubtRequest,
  DecideDoubtResponse,
  ApprovalDecideResponse,
  ApprovalRequestDto,
  NotificationListQueryArgs,
  NotificationListResponse,
  SlaReportResponse,
  PendingApprovalsResponse,
  AuditLogQuery,
  AuditLogResponseDto,
  ConventionListResponseDto,
  CurrentUserDto,
  GitTestConnectionRequest,
  GitTestConnectionResponse,
  EventSearchQuery,
  EventSearchResponseDto,
  FailureRecordDto,
  LoginRequest,
  PasswordChangeRequest,
  PasswordResetConfirmRequest,
  PasswordResetRequestRequest,
  PipelineRunCostDto,
  PipelineRunCreateRequest,
  PipelineRunCreateResponse,
  PipelineRunListItemDto,
  PipelineRunListQuery,
  PipelineRunReadDto,
  RunTraceResponse,
  StopRunResponse,
  AddMemberRequest,
  SetMemberStagesRequest,
  ChangeMemberRoleRequest,
  AdminUserDto,
  AdminUsersListResponse,
  CreateUserRequest,
  SetUserRoleRequest,
  ModelPriceListDto,
  ModelPriceProposalDto,
  ModelPriceApplyResultDto,
  ProjectConfigPayload,
  ProjectCreateRequest,
  ProjectCreateResponse,
  CredentialMaskDto,
  ProjectDetailDto,
  ProjectMemberDto,
  ProjectMembersResponseDto,
  RequirementUploadResponse,
  ProjectListQueryArgs,
  ProjectListResponseDto,
  ProjectListItemDto,
  PublicUserDto,
  RegisterRequest,
  ReplayRequest,
  ReplayResponse,
  SessionDto,
  StageDetailDto,
  StageListResponseDto,
  StallDetailDto,
  SuccessEnvelope,
  UserRole
} from '@/types/api';

const API_BASE_URL = import.meta.env['VITE_API_BASE_URL'] ?? 'http://localhost';

function unwrap<T>(envelope: SuccessEnvelope<T>): T {
  return envelope.data;
}

interface RawCurrentUser {
  user_id: string;
  email: string;
  system_role?: UserRole;
  role?: UserRole;
  display_name?: string | null;
  name?: string;
  capabilities?: ReadonlyArray<string>;
}

const ADMIN_CAPABILITIES: ReadonlyArray<string> = [
  'replay:dispatch',
  'approval:decide',
  'pipelines:read',
  'projects:read',
  'project:create',
  'pipeline.create',
  'audit_log:read'
];
const SUBMITTER_CAPABILITIES: ReadonlyArray<string> = [
  'pipelines:read',
  'projects:read',
  'pipeline.create'
];
const APPROVER_CAPABILITIES: ReadonlyArray<string> = [
  ...SUBMITTER_CAPABILITIES,
  'approval:decide'
];

function fallbackCapabilities(role: UserRole): ReadonlyArray<string> {
  if (role === 'admin') return ADMIN_CAPABILITIES;
  if (role === 'approver') return APPROVER_CAPABILITIES;
  return SUBMITTER_CAPABILITIES;
}

function normalizeCurrentUser(raw: RawCurrentUser): CurrentUserDto {
  const role: UserRole = raw.role ?? raw.system_role ?? 'task_submitter';
  const capabilities = raw.capabilities ?? fallbackCapabilities(role);
  const name = raw.name ?? raw.display_name ?? undefined;
  return {
    user_id: raw.user_id,
    email: raw.email,
    role,
    capabilities,
    ...(name != null ? { name } : {})
  };
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: `${API_BASE_URL}/api/v1`,
  prepareHeaders: (headers) => {
    const token = localStorage.getItem('jwt');
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return headers;
  }
});

let refreshInFlight: Promise<string | null> | null = null;

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, apiCtx, extraOptions) => {
  let result = await rawBaseQuery(args, apiCtx, extraOptions);

  if (result.error?.status === 401) {
    const url = typeof args === 'string' ? args : args.url;
    const isBusinessAuth401 =
      url.includes('/auth/refresh') ||
      url.includes('/auth/login') ||
      url.includes('/auth/password-change') ||
      url.includes('/auth/password-reset');
    const refreshToken = localStorage.getItem('jwt_refresh');

    if (!isBusinessAuth401 && refreshToken) {
      if (!refreshInFlight) {
        refreshInFlight = (async (): Promise<string | null> => {
          const refreshResult = await rawBaseQuery(
            { url: '/auth/refresh', method: 'POST', body: { refresh_token: refreshToken } },
            apiCtx,
            extraOptions
          );
          const data = (refreshResult.data as { data?: SessionDto } | undefined)?.data;
          if (data?.access_token) {
            apiCtx.dispatch(
              sessionEstablished({ token: data.access_token, refreshToken: data.refresh_token })
            );
            return data.access_token;
          }
          return null;
        })().finally(() => {
          refreshInFlight = null;
        });
      }
      const newToken = await refreshInFlight;
      if (newToken) {
        result = await rawBaseQuery(args, apiCtx, extraOptions);
      } else {
        apiCtx.dispatch(sessionExpiredAction());
      }
    }
  }
  return result;
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'PipelineRun',
    'PipelineRunList',
    'StageList',
    'StageDetail',
    'EventSearch',
    'RunCost',
    'RunFailure',
    'RunStall',
    'RunApproval',
    'RunTrace',
    'CurrentUser',
    'ProjectList',
    'ProjectDetail',
    'ProjectMembers',
    'ProjectCredential',
    'ProjectConfig',
    'Conventions',
    'AuditLog',
    'AdminUsers',
    'Notifications',
    'SlaReport',
    'ModelPrices'
  ],
  endpoints: (build) => ({
    listPipelineRuns: build.query<
      { items: ReadonlyArray<PipelineRunListItemDto>; total: number; page: number; per_page: number },
      PipelineRunListQuery
    >({
      query: (q) => ({
        url: '/pipelines/runs',
        params: buildParams(q as Record<string, unknown>)
      }),
      transformResponse: (resp: SuccessEnvelope<ReadonlyArray<PipelineRunListItemDto>>) => {
        const meta = resp.meta && 'page' in resp.meta ? resp.meta : null;
        return {
          items: resp.data,
          total: meta?.total ?? resp.data.length,
          page: meta?.page ?? 1,
          per_page: meta?.per_page ?? resp.data.length
        };
      },
      providesTags: ['PipelineRunList']
    }),
    getPipelineRun: build.query<PipelineRunReadDto, string>({
      query: (id) => `/pipelines/runs/${encodeURIComponent(id)}`,
      transformResponse: (resp: SuccessEnvelope<PipelineRunReadDto>) => unwrap(resp),
      providesTags: (_r, _e, id) => [{ type: 'PipelineRun', id }]
    }),
    listStages: build.query<StageListResponseDto, string>({
      query: (runId) => `/pipelines/runs/${encodeURIComponent(runId)}/stages`,
      transformResponse: (resp: SuccessEnvelope<StageListResponseDto>) => unwrap(resp),
      providesTags: (_r, _e, id) => [{ type: 'StageList', id }]
    }),
    searchEvents: build.query<EventSearchResponseDto, EventSearchQuery>({
      query: (q) => ({
        url: '/events/search',
        params: buildParams(q as Record<string, unknown>)
      }),
      transformResponse: (resp: SuccessEnvelope<EventSearchResponseDto>) => unwrap(resp),
      providesTags: ['EventSearch']
    }),

    getStageDetail: build.query<StageDetailDto, { runId: string; stageId: string }>({
      query: ({ runId, stageId }) =>
        `/pipelines/runs/${encodeURIComponent(runId)}/stages/${encodeURIComponent(stageId)}`,
      transformResponse: (resp: SuccessEnvelope<unknown>) =>
        normalizeStageDetail(unwrap(resp) as Parameters<typeof normalizeStageDetail>[0]),
      providesTags: (_r, _e, arg) => [{ type: 'StageDetail', id: `${arg.runId}:${arg.stageId}` }]
    }),

    getArtefact: build.query<string, string>({
      query: (ref) => ({
        url: `/artefacts/${encodeURIComponent(ref)}`,
        responseHandler: 'text'
      })
    }),

    getRunCost: build.query<PipelineRunCostDto, string>({
      query: (runId) => `/pipelines/runs/${encodeURIComponent(runId)}/cost`,
      transformResponse: (resp: SuccessEnvelope<PipelineRunCostDto>) => unwrap(resp),
      providesTags: (_r, _e, id) => [{ type: 'RunCost', id }]
    }),

    getRunFailure: build.query<FailureRecordDto, string>({
      query: (runId) => `/pipelines/runs/${encodeURIComponent(runId)}/failure-record`,
      transformResponse: (resp: SuccessEnvelope<FailureRecordDto>) => unwrap(resp),
      providesTags: (_r, _e, id) => [{ type: 'RunFailure', id }]
    }),

    getRunStall: build.query<StallDetailDto, string>({
      query: (runId) => `/pipelines/runs/${encodeURIComponent(runId)}/stall`,
      transformResponse: (resp: SuccessEnvelope<StallDetailDto>) => unwrap(resp),
      providesTags: (_r, _e, id) => [{ type: 'RunStall', id }]
    }),

    getRunApproval: build.query<ApprovalRequestDto, string>({
      query: (runId) => `/pipelines/runs/${encodeURIComponent(runId)}/approval`,
      transformResponse: (resp: SuccessEnvelope<ApprovalRequestDto>) => unwrap(resp),
      providesTags: (_r, _e, id) => [{ type: 'RunApproval', id }]
    }),

    decideApproval: build.mutation<ApprovalDecideResponse, ApprovalDecideRequest>({
      query: (body) => ({
        url: '/approvals/decide',
        method: 'POST',
        body
      }),
      transformResponse: (resp: SuccessEnvelope<ApprovalDecideResponse>) => unwrap(resp),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'RunApproval', id: arg.approval_id },
        'PipelineRunList'
      ]
    }),

    raiseDoubt: build.mutation<RaiseDoubtResponse, RaiseDoubtRequest>({
      query: (body) => ({ url: '/approvals/raise_doubt', method: 'POST', body }),
      transformResponse: (resp: SuccessEnvelope<RaiseDoubtResponse>) => unwrap(resp),
      invalidatesTags: (_r, _e, arg) => [{ type: 'RunApproval', id: arg.approval_id }]
    }),

    doubtStatus: build.query<DoubtStatusResponse, string>({
      query: (approval_id) =>
        `/approvals/doubt_status?approval_id=${encodeURIComponent(approval_id)}`,
      transformResponse: (resp: SuccessEnvelope<DoubtStatusResponse>) => unwrap(resp),
      providesTags: (_r, _e, id) => [{ type: 'RunApproval', id }]
    }),

    decideDoubt: build.mutation<DecideDoubtResponse, DecideDoubtRequest>({
      query: (body) => ({ url: '/approvals/decide_doubt', method: 'POST', body }),
      transformResponse: (resp: SuccessEnvelope<DecideDoubtResponse>) => unwrap(resp),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'RunApproval', id: arg.approval_id },
        'PipelineRunList',
        'Notifications'
      ]
    }),

    listNotifications: build.query<NotificationListResponse, NotificationListQueryArgs | void>({
      query: (arg) => {
        const a = (arg ?? {}) as NotificationListQueryArgs;
        const qs = new URLSearchParams();
        if (a.onlyUnread) qs.set('only_unread', 'true');
        if (a.page_size !== undefined) qs.set('page_size', String(a.page_size));
        if (a.offset !== undefined && a.offset > 0) qs.set('offset', String(a.offset));
        const q = qs.toString();
        return q ? `/notifications?${q}` : '/notifications';
      },
      transformResponse: (resp: SuccessEnvelope<NotificationListResponse>) => unwrap(resp),
      providesTags: ['Notifications']
    }),
    markNotificationRead: build.mutation<{ notification_id: string; read_at?: string }, string>({
      query: (id) => ({ url: `/notifications/${encodeURIComponent(id)}/read`, method: 'POST' }),
      transformResponse: (resp: SuccessEnvelope<{ notification_id: string; read_at?: string }>) =>
        unwrap(resp),
      invalidatesTags: ['Notifications']
    }),

    getPendingApprovalsForMe: build.query<PendingApprovalsResponse, void>({
      query: () => '/approvals/pending-for-me',
      transformResponse: (resp: SuccessEnvelope<PendingApprovalsResponse>) => unwrap(resp),
      providesTags: ['RunApproval']
    }),

    getSlaReport: build.query<SlaReportResponse, { from?: string; to?: string } | void>({
      query: (arg) => {
        const params = new URLSearchParams();
        if (arg && arg.from) params.set('from', arg.from);
        if (arg && arg.to) params.set('to', arg.to);
        const qs = params.toString();
        return `/approvals/sla-report${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (resp: SuccessEnvelope<SlaReportResponse>) => unwrap(resp),
      providesTags: ['SlaReport']
    }),

    getRunTraces: build.query<RunTraceResponse, { runId: string; offset?: number; limit?: number }>({
      query: ({ runId, offset, limit }) => {
        const qs = new URLSearchParams();
        if (offset !== undefined) qs.set('offset', String(offset));
        if (limit !== undefined) qs.set('limit', String(limit));
        const q = qs.toString();
        return `/pipelines/runs/${encodeURIComponent(runId)}/traces${q ? `?${q}` : ''}`;
      },
      transformResponse: (resp: SuccessEnvelope<RunTraceResponse>) => unwrap(resp),
      providesTags: (_r, _e, arg) => [{ type: 'RunTrace', id: arg.runId }]
    }),

    stopRun: build.mutation<StopRunResponse, { pipeline_run_id: string }>({
      query: (body) => ({
        url: `/pipelines/runs/${encodeURIComponent(body.pipeline_run_id)}/stop`,
        method: 'POST',
        body: {}
      }),
      transformResponse: (resp: SuccessEnvelope<StopRunResponse>) => unwrap(resp),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'PipelineRun', id: arg.pipeline_run_id },
        { type: 'StageList', id: arg.pipeline_run_id },
        'PipelineRunList'
      ]
    }),

    replayFromFailedStage: build.mutation<ReplayResponse, ReplayRequest>({
      query: (body) => ({
        url: `/pipelines/runs/${encodeURIComponent(body.pipeline_run_id)}/resume`,
        method: 'POST',
        body: { from_stage: body.from_stage }
      }),
      transformResponse: (resp: SuccessEnvelope<ReplayResponse>) => unwrap(resp),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'PipelineRun', id: arg.pipeline_run_id },
        { type: 'RunFailure', id: arg.pipeline_run_id },
        'PipelineRunList'
      ]
    }),

    getCurrentUser: build.query<CurrentUserDto, void>({
      query: () => '/users/me',
      transformResponse: (resp: SuccessEnvelope<RawCurrentUser>) =>
        normalizeCurrentUser(unwrap(resp)),
      providesTags: ['CurrentUser']
    }),

    updateProfile: build.mutation<CurrentUserDto, { display_name: string }>({
      query: (body) => ({ url: '/users/me', method: 'PUT', body }),
      transformResponse: (resp: SuccessEnvelope<RawCurrentUser>) =>
        normalizeCurrentUser(unwrap(resp)),
      invalidatesTags: ['CurrentUser']
    }),


    login: build.mutation<SessionDto, LoginRequest>({
      query: (body) => ({ url: '/auth/login', method: 'POST', body }),
      transformResponse: (resp: SuccessEnvelope<SessionDto>) => unwrap(resp),
      onQueryStarted: async (_arg, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            sessionEstablished({ token: data.access_token, refreshToken: data.refresh_token })
          );
        } catch {
        }
      },
      invalidatesTags: ['CurrentUser', 'ProjectList']
    }),

    logout: build.mutation<{ ok: true }, void>({
      query: () => ({ url: '/auth/logout', method: 'POST' }),
      transformResponse: () => ({ ok: true as const })
    }),

    refreshSession: build.mutation<SessionDto, { refresh_token: string }>({
      query: (body) => ({ url: '/auth/refresh', method: 'POST', body }),
      transformResponse: (resp: SuccessEnvelope<SessionDto>) => unwrap(resp),
      onQueryStarted: async (_arg, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            sessionEstablished({ token: data.access_token, refreshToken: data.refresh_token })
          );
        } catch {
        }
      },
      invalidatesTags: ['CurrentUser']
    }),

    register: build.mutation<PublicUserDto, RegisterRequest>({
      query: (body) => ({ url: '/auth/register', method: 'POST', body }),
      transformResponse: (resp: SuccessEnvelope<PublicUserDto>) => unwrap(resp)
    }),

    requestPasswordReset: build.mutation<{ ok: true }, PasswordResetRequestRequest>({
      query: (body) => ({ url: '/auth/password-reset/request', method: 'POST', body }),
      transformResponse: () => ({ ok: true as const })
    }),

    confirmPasswordReset: build.mutation<{ ok: true }, PasswordResetConfirmRequest>({
      query: (body) => ({ url: '/auth/password-reset/confirm', method: 'POST', body }),
      transformResponse: () => ({ ok: true as const })
    }),

    changePassword: build.mutation<{ ok: true }, PasswordChangeRequest>({
      query: (body) => ({ url: '/auth/password-change', method: 'POST', body }),
      transformResponse: () => ({ ok: true as const })
    }),


    listProjects: build.query<ProjectListResponseDto, ProjectListQueryArgs | void>({
      query: (args) => {
        const a = (args ?? {}) as ProjectListQueryArgs;
        const qs = new URLSearchParams();
        if (a.page_size !== undefined) qs.set('page_size', String(a.page_size));
        if (a.offset !== undefined && a.offset > 0) qs.set('offset', String(a.offset));
        if (a.search) qs.set('search', a.search);
        const q = qs.toString();
        return q ? `/projects?${q}` : '/projects';
      },
      transformResponse: (resp: SuccessEnvelope<unknown>): ProjectListResponseDto => {
        const data = unwrap(resp) as unknown;
        const rows = Array.isArray(data)
          ? (data as Array<Record<string, unknown>>)
          : ((data as { projects?: Array<Record<string, unknown>> })?.projects ?? []);
        const envelope = (Array.isArray(data) ? {} : data ?? {}) as Record<string, unknown>;
        return {
          ...(typeof envelope['total'] === 'number' ? { total: envelope['total'] } : {}),
          ...(typeof envelope['has_more'] === 'boolean'
            ? { has_more: envelope['has_more'] }
            : {}),
          projects: rows.map((p) => ({
            project_id: String(p['project_id'] ?? ''),
            slug: String(p['slug'] ?? ''),
            name: String(p['name'] ?? ''),
            member_role: (p['member_role'] as UserRole) ?? 'admin',
            member_count: Number(p['member_count'] ?? 1),
            last_run_state: (p['last_run_state'] as ProjectListItemDto['last_run_state']) ?? null,
            last_run_at: (p['last_run_at'] as string | null) ?? null,
            has_active_run: Boolean(p['has_active_run'] ?? false),
            updated_at: String(p['updated_at'] ?? p['created_at'] ?? '')
          }))
        };
      },
      providesTags: ['ProjectList']
    }),

    getProjectCosts: build.query<Record<string, number>, void>({
      query: () => '/projects/costs',
      transformResponse: (resp: SuccessEnvelope<unknown>): Record<string, number> => {
        const data = unwrap(resp) as unknown;
        if (data === null || typeof data !== 'object' || Array.isArray(data)) return {};
        const out: Record<string, number> = {};
        for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
          if (typeof v === 'number' && Number.isFinite(v)) out[k] = v;
        }
        return out;
      },
      providesTags: ['ProjectList']
    }),


    createProject: build.mutation<ProjectCreateResponse, ProjectCreateRequest>({
      query: ({ git_url, ...rest }) => ({
        url: '/projects',
        method: 'POST',
        body: { ...rest, ...(git_url !== undefined ? { git_repository_url: git_url } : {}) }
      }),
      transformResponse: (
        resp: SuccessEnvelope<{ project: { project_id: string; slug: string }; credential_id?: string }>
      ): ProjectCreateResponse => {
        const data = unwrap(resp);
        return { project_id: data.project.project_id, slug: data.project.slug };
      },
      invalidatesTags: ['ProjectList']
    }),

    listModelPrices: build.query<ModelPriceListDto, void>({
      query: () => ({ url: '/model-prices' }),
      transformResponse: (resp: SuccessEnvelope<ModelPriceListDto>) => unwrap(resp),
      providesTags: ['ModelPrices']
    }),

    fetchModelPrices: build.mutation<ModelPriceProposalDto, void>({
      query: () => ({ url: '/model-prices/fetch', method: 'POST' }),
      transformResponse: (resp: SuccessEnvelope<ModelPriceProposalDto>) => unwrap(resp)
    }),

    applyModelPrices: build.mutation<
      ModelPriceApplyResultDto,
      { proposal_id: string; models: ReadonlyArray<string> }
    >({
      query: (body) => ({ url: '/model-prices/apply', method: 'POST', body }),
      transformResponse: (resp: SuccessEnvelope<ModelPriceApplyResultDto>) => unwrap(resp),
      invalidatesTags: ['ModelPrices', 'ProjectConfig']
    }),

    setModelSelectable: build.mutation<
      { models: ReadonlyArray<{ model_id: string; selectable: boolean }>; updated_at: string },
      { models: ReadonlyArray<{ model_id: string; selectable: boolean }> }
    >({
      query: (body) => ({ url: '/model-prices/selectable', method: 'POST', body }),
      transformResponse: (
        resp: SuccessEnvelope<{
          models: ReadonlyArray<{ model_id: string; selectable: boolean }>;
          updated_at: string;
        }>
      ) => unwrap(resp),
      invalidatesTags: ['ModelPrices', 'ProjectConfig']
    }),

    getProjectConfigs: build.query<
      ReadonlyArray<{ key: string; value: unknown; source: string }>,
      string
    >({
      query: (projectId) => ({ url: `/projects/${encodeURIComponent(projectId)}/configs` }),
      transformResponse: (
        resp: SuccessEnvelope<ReadonlyArray<{ key: string; value: unknown; source: string }>>
      ) => unwrap(resp),
      providesTags: ['ProjectConfig']
    }),

    setProjectConfigs: build.mutation<
      { ok: true },
      { projectId: string; body: ProjectConfigPayload }
    >({
      invalidatesTags: ['ProjectConfig'],
      queryFn: async ({ projectId, body }, _api, _opts, baseQuery) => {
        const base = `/projects/${encodeURIComponent(projectId)}/configs`;
        const puts: Array<{ key: string; value: unknown }> = [];
        for (const [role, cfg] of Object.entries(body.agent_config ?? {})) {
          puts.push({ key: `agent.${role}.reasoning_effort`, value: cfg.reasoning_effort });
          if (cfg.pause_after !== undefined) {
            puts.push({ key: `agent.${role}.pause_after`, value: cfg.pause_after });
          }
          if (cfg.approval_sla_minutes !== undefined) {
            puts.push({ key: `agent.${role}.approval_sla_minutes`, value: cfg.approval_sla_minutes });
          }
          if (cfg.model !== undefined) {
            puts.push({ key: `agent.${role}.model`, value: cfg.model });
          }
        }
        for (const [role, policy] of Object.entries(body.retry_policy ?? {})) {
          puts.push({ key: `agent.${role}.retry_policy`, value: policy });
        }
        if (body.max_conflict_retries !== undefined) {
          puts.push({ key: 'pipeline.max_conflict_retries', value: body.max_conflict_retries });
        }
        let applied = 0;
        for (const { key, value } of puts) {
          const res = await baseQuery({
            url: `${base}/${encodeURIComponent(key)}`,
            method: 'PUT',
            body: { value }
          });
          if (res.error) {
            const err = res.error as { status?: unknown; data?: { errors?: Array<{ message?: string }> } };
            const detail = err.data?.errors?.[0];
            const baseMsg = detail?.message ?? 'save failed';
            const partial =
              applied > 0 ? ` (${applied} of ${puts.length} settings were saved before this)` : '';
            return {
              error: {
                ...(res.error as object),
                data: {
                  data: null,
                  errors: [{ code: 'VALIDATION_ERROR', message: `${key}: ${baseMsg}${partial}` }]
                }
              }
            } as typeof res;
          }
          applied += 1;
        }
        return { data: { ok: true as const } };
      }
    }),



    getProjectDetail: build.query<ProjectDetailDto, string>({
      query: (projectId) => `/projects/${encodeURIComponent(projectId)}`,
      transformResponse: (resp: SuccessEnvelope<unknown>): ProjectDetailDto => {
        const p = unwrap(resp) as Record<string, unknown>;
        return {
          project_id: String(p['project_id'] ?? ''),
          slug: String(p['slug'] ?? ''),
          name: String(p['name'] ?? ''),
          description: String(p['description'] ?? ''),
          git_url: (p['git_url'] as string | null) ?? (p['git_repository_url'] as string | null) ?? null,
          ...(typeof p['git_username'] === 'string' ? { git_username: p['git_username'] } : {}),
          ...(p['git_auth_mode'] === 'bearer' || p['git_auth_mode'] === 'basic'
            ? { git_auth_mode: p['git_auth_mode'] }
            : {}),
          git_protocol: (p['git_protocol'] as ProjectDetailDto['git_protocol']) ?? null,
          ...(typeof p['default_branch'] === 'string'
            ? { default_branch: p['default_branch'] }
            : {}),
          tech_stack: (p['tech_stack'] as ProjectDetailDto['tech_stack']) ?? null,
          tech_stack_editable: Boolean(p['tech_stack_editable'] ?? false),
          member_role: (p['member_role'] as ProjectDetailDto['member_role']) ?? 'admin'
        };
      },
      providesTags: (_r, _e, id) => [{ type: 'ProjectDetail', id }]
    }),

    updateProject: build.mutation<
      ProjectDetailDto,
      {
        projectId: string;
        body: Partial<{
          name: string;
          description: string;
          default_branch: string;
          git_username: string;
          git_repository_url: string;
          tech_stack: ProjectDetailDto['tech_stack'];
        }>;
      }
    >({
      query: ({ projectId, body }) => ({
        url: `/projects/${encodeURIComponent(projectId)}`,
        method: 'PUT',
        body
      }),
      transformResponse: (resp: SuccessEnvelope<ProjectDetailDto>) => unwrap(resp),
      invalidatesTags: (_r, _e, arg) => [{ type: 'ProjectDetail', id: arg.projectId }, 'ProjectList']
    }),

    listMembers: build.query<ProjectMembersResponseDto, string>({
      query: (projectId) => `/projects/${encodeURIComponent(projectId)}/members`,
      transformResponse: (resp: SuccessEnvelope<ReadonlyArray<ProjectMemberDto>>) => ({
        members: unwrap(resp)
      }),
      providesTags: (_r, _e, id) => [{ type: 'ProjectMembers', id }]
    }),
    addMember: build.mutation<{ ok: true }, { projectId: string; body: AddMemberRequest }>({
      query: ({ projectId, body }) => ({
        url: `/projects/${encodeURIComponent(projectId)}/members`,
        method: 'POST',
        body
      }),
      transformResponse: () => ({ ok: true as const }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'ProjectMembers', id: arg.projectId }]
    }),
    setMemberStages: build.mutation<
      { ok: true },
      { projectId: string; userId: string; body: SetMemberStagesRequest }
    >({
      query: ({ projectId, userId, body }) => ({
        url: `/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}/stages`,
        method: 'PUT',
        body
      }),
      transformResponse: () => ({ ok: true as const }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'ProjectMembers', id: arg.projectId }]
    }),
    getProjectCredential: build.query<
      CredentialMaskDto,
      { projectId: string; credential_type: 'codex_api_key' | 'git_https_token' }
    >({
      query: ({ projectId, credential_type }) =>
        `/projects/${encodeURIComponent(projectId)}/credentials/${encodeURIComponent(credential_type)}`,
      transformResponse: (resp: SuccessEnvelope<CredentialMaskDto>) => unwrap(resp),
      providesTags: (_r, _e, arg) => [{ type: 'ProjectCredential', id: `${arg.projectId}:${arg.credential_type}` }]
    }),

    setProjectCredential: build.mutation<
      { credential_id: string },
      { projectId: string; credential_type: 'codex_api_key' | 'git_https_token'; plaintext: string }
    >({
      query: ({ projectId, credential_type, plaintext }) => ({
        url: `/projects/${encodeURIComponent(projectId)}/credentials`,
        method: 'POST',
        body: { credential_type, plaintext }
      }),
      transformResponse: (resp: SuccessEnvelope<{ credential_id: string }>) => unwrap(resp),
      invalidatesTags: (_r, _e, arg) => [{ type: 'ProjectCredential', id: `${arg.projectId}:${arg.credential_type}` }]
    }),
    changeMemberRole: build.mutation<
      { ok: true },
      { projectId: string; userId: string; body: ChangeMemberRoleRequest }
    >({
      query: ({ projectId, userId, body }) => ({
        url: `/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`,
        method: 'PUT',
        body
      }),
      transformResponse: () => ({ ok: true as const }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'ProjectMembers', id: arg.projectId },
        'AdminUsers',
        'CurrentUser'
      ]
    }),
    removeMember: build.mutation<{ ok: true }, { projectId: string; userId: string }>({
      query: ({ projectId, userId }) => ({
        url: `/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`,
        method: 'DELETE'
      }),
      transformResponse: () => ({ ok: true as const }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'ProjectMembers', id: arg.projectId }]
    }),

    listUsers: build.query<AdminUsersListResponse, void>({
      query: () => '/users',
      transformResponse: (resp: SuccessEnvelope<AdminUsersListResponse | AdminUserDto[]>) => {
        const data = unwrap(resp) as AdminUsersListResponse | AdminUserDto[];
        return Array.isArray(data) ? { users: data } : data;
      },
      providesTags: ['AdminUsers']
    }),
    createUser: build.mutation<AdminUserDto, CreateUserRequest>({
      query: (body) => ({ url: '/users', method: 'POST', body }),
      transformResponse: (resp: SuccessEnvelope<AdminUserDto>) => unwrap(resp),
      invalidatesTags: ['AdminUsers']
    }),
    setUserRole: build.mutation<AdminUserDto, SetUserRoleRequest>({
      query: ({ user_id, role }) => ({
        url: `/users/${encodeURIComponent(user_id)}/role`,
        method: 'PUT',
        body: { role }
      }),
      transformResponse: (resp: SuccessEnvelope<AdminUserDto>) => unwrap(resp),
      invalidatesTags: ['AdminUsers', 'ProjectMembers', 'CurrentUser']
    }),


    testGitConnection: build.mutation<
      GitTestConnectionResponse,
      { projectId: string; body: GitTestConnectionRequest }
    >({
      query: ({ projectId, body }) => ({
        url: `/projects/${encodeURIComponent(projectId)}/git/test-connection`,
        method: 'POST',
        body: {
          git_repository_url: body.git_url,
          git_protocol: body.protocol,
          ...(body.git_username !== undefined ? { git_username: body.git_username } : {}),
          ...(body.git_auth_mode !== undefined ? { git_auth_mode: body.git_auth_mode } : {}),
          ...(body.credential !== ''
            ? {
                inline_credential: {
                  credential_type: 'git_https_token',
                  plaintext: body.credential
                },
                save_on_success: true
              }
            : {})
        }
      }),
      transformResponse: (resp: SuccessEnvelope<GitTestConnectionResponse>) => {
        const r = unwrap(resp);
        return r.credential_id === undefined && r.saved_credential_id !== undefined
          ? { ...r, credential_id: r.saved_credential_id }
          : r;
      }
    }),

    testGitConnectionDraft: build.mutation<GitTestConnectionResponse, GitTestConnectionRequest>({
      query: (body) => ({
        url: '/git/test-connection',
        method: 'POST',
        body: {
          git_repository_url: body.git_url,
          git_protocol: body.protocol,
          ...(body.git_username !== undefined ? { git_username: body.git_username } : {}),
          ...(body.git_auth_mode !== undefined ? { git_auth_mode: body.git_auth_mode } : {}),
          credential: body.credential
        }
      }),
      transformResponse: (resp: SuccessEnvelope<GitTestConnectionResponse>) => unwrap(resp)
    }),

    listConventions: build.query<ConventionListResponseDto, string>({
      query: (projectId) => `/projects/${encodeURIComponent(projectId)}/conventions`,
      transformResponse: (resp: SuccessEnvelope<ConventionListResponseDto>) => unwrap(resp),
      providesTags: (_r, _e, projectId) => [{ type: 'Conventions', id: projectId }]
    }),

    uploadConvention: build.mutation<
      { convention_id: string; file_name: string; convention_type: string },
      { projectId: string; file: File; conventionType?: string; appliesTo?: ReadonlyArray<string> }
    >({
      query: ({ projectId, file, conventionType, appliesTo }) => {
        const form = new FormData();
        form.append('file', file);
        const params: Record<string, string> = { filename: file.name };
        if (conventionType) params['convention_type'] = conventionType;
        if (appliesTo && appliesTo.length > 0) params['applies_to'] = appliesTo.join(',');
        return {
          url: `/projects/${encodeURIComponent(projectId)}/conventions`,
          method: 'POST',
          params,
          body: form
        };
      },
      transformResponse: (resp: SuccessEnvelope<{ convention_id: string; file_name: string; convention_type: string }>) =>
        unwrap(resp),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Conventions', id: arg.projectId }]
    }),

    deleteConvention: build.mutation<{ convention_id: string; deleted: boolean }, { projectId: string; conventionId: string }>({
      query: ({ projectId, conventionId }) => ({
        url: `/projects/${encodeURIComponent(projectId)}/conventions/${encodeURIComponent(conventionId)}`,
        method: 'DELETE'
      }),
      transformResponse: (resp: SuccessEnvelope<{ convention_id: string; deleted: boolean }>) => unwrap(resp),
      invalidatesTags: (_r, _e, arg) => [{ type: 'Conventions', id: arg.projectId }]
    }),


    createPipelineRun: build.mutation<PipelineRunCreateResponse, PipelineRunCreateRequest>({
      query: (body) => ({ url: '/pipelines/runs', method: 'POST', body }),
      transformResponse: (resp: SuccessEnvelope<PipelineRunCreateResponse>) => unwrap(resp),
      invalidatesTags: ['PipelineRunList']
    }),

    uploadDoubtRequirement: build.mutation<
      RequirementUploadResponse,
      { approvalId: string; file: File }
    >({
      query: ({ approvalId, file }) => {
        const form = new FormData();
        form.append('file', file);
        return {
          url: `/approvals/${encodeURIComponent(approvalId)}/requirement-uploads`,
          method: 'POST',
          params: { filename: file.name },
          body: form
        };
      },
      transformResponse: (resp: SuccessEnvelope<RequirementUploadResponse>) => unwrap(resp)
    }),

    uploadRequirement: build.mutation<RequirementUploadResponse, { projectId: string; file: File }>({
      query: ({ projectId, file }) => {
        const form = new FormData();
        form.append('file', file);
        return {
          url: `/projects/${encodeURIComponent(projectId)}/requirement-uploads`,
          method: 'POST',
          params: { filename: file.name },
          body: form
        };
      },
      transformResponse: (resp: SuccessEnvelope<RequirementUploadResponse>) => unwrap(resp)
    }),


    searchAuditLog: build.query<AuditLogResponseDto, AuditLogQuery>({
      query: (q) => ({
        url: '/audit/events',
        params: buildParams(q as unknown as Record<string, unknown>)
      }),
      transformResponse: (resp: SuccessEnvelope<AuditLogResponseDto>) => unwrap(resp),
      providesTags: ['AuditLog'],
      keepUnusedDataFor: 0
    })
  })
});

function buildParams(q: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = String(v);
  }
  return out;
}

export const {
  useListPipelineRunsQuery,
  useGetPipelineRunQuery,
  useListStagesQuery,
  useSearchEventsQuery,
  useGetStageDetailQuery,
  useLazyGetArtefactQuery,
  useGetRunCostQuery,
  useGetProjectCostsQuery,
  useGetRunFailureQuery,
  useGetRunStallQuery,
  useGetRunApprovalQuery,
  useDecideApprovalMutation,
  useRaiseDoubtMutation,
  useDoubtStatusQuery,
  useDecideDoubtMutation,
  useListNotificationsQuery,
  useMarkNotificationReadMutation,
  useGetSlaReportQuery,
  useGetPendingApprovalsForMeQuery,
  useReplayFromFailedStageMutation,
  useGetRunTracesQuery,
  useLazyGetRunTracesQuery,
  useStopRunMutation,
  useGetCurrentUserQuery,
  useUpdateProfileMutation,
  useLoginMutation,
  useLogoutMutation,
  useRefreshSessionMutation,
  useRegisterMutation,
  useRequestPasswordResetMutation,
  useConfirmPasswordResetMutation,
  useChangePasswordMutation,
  useListProjectsQuery,
  useSearchAuditLogQuery,
  useCreatePipelineRunMutation,
  useUploadRequirementMutation,
  useUploadDoubtRequirementMutation,
  useTestGitConnectionMutation,
  useTestGitConnectionDraftMutation,
  useListConventionsQuery,
  useUploadConventionMutation,
  useDeleteConventionMutation,
  useCreateProjectMutation,
  useGetProjectConfigsQuery,
  useSetProjectConfigsMutation,
  useGetProjectDetailQuery,
  useUpdateProjectMutation,
  useListMembersQuery,
  useAddMemberMutation,
  useSetMemberStagesMutation,
  useChangeMemberRoleMutation,
  useRemoveMemberMutation,
  useListUsersQuery,
  useCreateUserMutation,
  useSetUserRoleMutation,
  useGetProjectCredentialQuery,
  useSetProjectCredentialMutation,
  useListModelPricesQuery,
  useFetchModelPricesMutation,
  useApplyModelPricesMutation,
  useSetModelSelectableMutation
} = api;
